import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { useTranslation } from 'react-i18next';

const AdminLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();

      if (!userDoc.exists()) {
        if (user.email === 'jkuibia@gmail.com') {
          await setDoc(doc(db, 'users', user.uid), {
            email: user.email,
            firstName: 'Jordan',
            lastName: 'Kuibia',
            role: 'admin',
            createdAt: new Date()
          });
          navigate('/admin/dashboard');
        } else if (user.email === 'desireeafricanbraiding@gmail.com') {
          await setDoc(doc(db, 'users', user.uid), {
            email: user.email,
            firstName: 'Desiree',
            lastName: '',
            role: 'admin',
            createdAt: new Date()
          });
          navigate('/admin/dashboard');
        } else {
          setError(t('adminLogin.errorUnauthorized'));
          await auth.signOut();
          setLoading(false);
        }
      } else if (userData?.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        setError(t('adminLogin.errorUnauthorized'));
        await auth.signOut();
        setLoading(false);
      }
    } catch (error: any) {
      setError(t('adminLogin.errorCredentials'));
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            {t('adminLogin.title')}
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
            {t('adminLogin.subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin}>
            <TextField
              fullWidth
              label={t('adminLogin.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              disabled={loading}
            />
            <TextField
              fullWidth
              label={t('adminLogin.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              disabled={loading}
            />
            <Box sx={{ position: 'relative', mt: 3, mb: 2 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
              >
                {t('adminLogin.signIn')}
              </Button>
              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                  }}
                />
              )}
            </Box>
          </Box>

          <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 2 }}>
            {t('adminLogin.backToSitePrefix')}
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/')}
              sx={{ textTransform: 'none' }}
            >
              {t('adminLogin.backToSite')}
            </Button>
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminLoginPage;
